import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Box,
  Typography,
  makeStyles,
  TextField,
  InputAdornment,
  Button,
  Grid,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import SearchIcon from '@material-ui/icons/Search';
import GetAppIcon from '@material-ui/icons/GetApp';
import { getCurrentAndFutureTeamQuotasThunk } from '../redux/management.thunk';
import { selectCurrentAndFutureTeamQuotas } from '../redux/management.slice';
import { countOrACVFormatter } from '../../../util/customFunctions';
import TeamQuotaEditorModal from './TeamQuotaEditorModal';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    maxWidth: '1000px',
  },
  header: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  subHeader: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  searchField: {
    marginBottom: theme.spacing(2),
  },
  dataGridContainer: {
    height: '50vh',
    width: '100%',
    overflow: 'hidden',
    '& .MuiDataGrid-root': {
      width: '100%',
    },
    '& .MuiDataGrid-window': {
      left: 0,
      right: 0,
      bottom: 0,
      position: 'absolute',
      overflowX: 'hidden',
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#4472c4 !important',
      color: 'white !important',
    },
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: '#4472c4 !important',
      color: 'white !important',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold !important',
      color: 'white !important',
    },
    '& .MuiDataGrid-iconSeparator': {
      color: 'white !important',
    },
    '& .MuiDataGrid-sortIcon': {
      color: 'white !important',
    },
    '& .MuiDataGrid-menuIcon': {
      color: 'white !important',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      color: 'white !important',
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#e3ecfa !important',
      },
    },
  },
}));

const AddTeamQuotaView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const teamQuotas = useSelector(selectCurrentAndFutureTeamQuotas);
  const currencySymbol = useSelector(state => state.app.user.Currency);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await dispatch(getCurrentAndFutureTeamQuotasThunk());
      setLoading(false);
    };
    loadData();
  }, [dispatch]);

  const handleRowClick = (team) => {
    setSelectedTeam(team);
    setModalOpen(true);
  };

  const handleAddNewTeam = () => {
    setSelectedTeam(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedTeam(null);
  };

  const columns = [
    {
      field: 'Team',
      headerName: 'Team',
      flex: 1.5,
      minWidth: 200,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: 'Fiscal_Quarter',
      headerName: 'Fiscal Quarter',
      flex: 1,
      minWidth: 150,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: 'Quota',
      headerName: 'Quota',
      flex: 1,
      minWidth: 150,
      sortable: true,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => countOrACVFormatter(params.value, currencySymbol),
    },
    {
      field: 'NQP_GOAL',
      headerName: 'NQP Goal',
      flex: 1,
      minWidth: 150,
      sortable: true,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => countOrACVFormatter(params.value, currencySymbol),
    },
  ];

  const rows = useMemo(() => {
    if (!teamQuotas) return [];
    return teamQuotas.map((quota, index) => ({
      id: `${quota.Team}-${quota.Fiscal_Quarter}-${index}`,
      Team: quota.Team,
      Fiscal_Quarter: quota.Fiscal_Quarter,
      Quota: quota.Quota,
      NQP_GOAL: quota.NQP_GOAL,
    }));
  }, [teamQuotas]);

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const searchLower = searchTerm.toLowerCase();
    return rows.filter((row) => {
      return (
        row.Team?.toLowerCase().includes(searchLower) ||
        row.Fiscal_Quarter?.toLowerCase().includes(searchLower)
      );
    });
  }, [rows, searchTerm]);

  // CSV Export Function
  const handleExportCSV = () => {
    const headers = columns.map(col => col.headerName).join(',');
    const csvRows = filteredRows.map(row => {
      return columns.map(col => {
        const field = col.field;
        let value = row[field];

        // Apply formatter if exists
        if (col.valueFormatter && value !== undefined && value !== null) {
          value = col.valueFormatter({ value });
        }

        // Escape commas and quotes in CSV
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          value = `"${value.replace(/"/g, '""')}"`;
        }

        return value;
      }).join(',');
    });

    const csvContent = [headers, ...csvRows].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'team_quota_data.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Paper elevation={2} className={classes.container}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom={2}>
          <Box>
            <Typography variant="h2" className={classes.header}>
              Add Team Quota
            </Typography>
            <Typography variant="body1" className={classes.subHeader}>
              Select a team to add or modify quota assignments.
            </Typography>
          </Box>
          <Button variant="contained" color="primary" onClick={handleAddNewTeam}>
            Add New Team
          </Button>
        </Box>

        <Box display="flex" alignItems="center" gap={1} style={{ marginBottom: '16px' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search teams by name or fiscal quarter..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Download as CSV" arrow>
            <IconButton
              onClick={handleExportCSV}
              style={{
                padding: '8px',
                margin: '0 8px',
              }}
            >
              <GetAppIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <div className={classes.dataGridContainer}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={loading}
            onRowClick={(params) => handleRowClick(params.row.Team)}
            disableSelectionOnClick
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[10, 25, 50, 100]}
            pagination
            disableColumnMenu
          />
        </div>
      </Paper>

      <TeamQuotaEditorModal
        open={modalOpen}
        onClose={handleModalClose}
        selectedTeam={selectedTeam}
        existingQuotas={teamQuotas}
      />
    </>
  );
};

export default AddTeamQuotaView;
